import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database } from 'lucide-react';
import ManualDomainQueueForm from '../components/ManualDomainQueueForm';
import { clientLogger } from '../utils/logger';

/**
 * Page for manually queueing domains for processing
 */
const ManualDomainQueue = () => {
    const navigate = useNavigate();
    const [queuedDomains, setQueuedDomains] = useState([]);
    
    const handleDomainQueued = (result) => {
        clientLogger.debug('ManualDomainQueue: Domain queued', result);
        
        // Add to the list of queued domains
        setQueuedDomains(prev => [
            {
                id: result.jobId,
                domain: result.domain || 'Unknown domain',
                timestamp: new Date().toISOString(),
                alreadyQueued: result.alreadyQueued
            },
            ...prev
        ].slice(0, 10)); // Keep only the 10 most recent
    };
    
    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-4xl mx-auto px-4">
                <div className="mb-8 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft size={18} className="mr-1" />
                        Back
                    </button>
                    
                    <h1 className="text-2xl font-bold text-gray-900">
                        Manual Domain Processing
                    </h1>
                    
                    <div className="w-20"></div> {/* Spacer for alignment */}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                        <ManualDomainQueueForm onSuccess={handleDomainQueued} />
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                            <Database className="mr-2 text-blue-600" size={20} />
                            Recently Queued
                        </h2>
                        
                        {queuedDomains.length === 0 ? (
                            <p className="text-gray-500 text-sm italic">
                                No domains have been queued yet
                            </p>
                        ) : (
                            <ul className="divide-y divide-gray-200">
                                {queuedDomains.map(item => (
                                    <li key={item.id} className="py-3">
                                        <div className="flex flex-col">
                                            <span className="font-medium">{item.domain}</span>
                                            <div className="flex justify-between items-center mt-1">
                                                <span className="text-xs text-gray-500">
                                                    {new Date(item.timestamp).toLocaleTimeString()}
                                                </span>
                                                {item.alreadyQueued && (
                                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                                        Re-queued
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-800 mb-2">About Domain Processing</h3>
                    <p className="text-sm text-blue-700">
                        When you add a domain to the processing queue, our system will attempt to gather 
                        brand information about the domain, including company details, logos, and other 
                        relevant data. This process runs in the background and may take a few minutes to complete.
                    </p>
                    <p className="text-sm text-blue-700 mt-2">
                        Domains are normally only processed once every 5 minutes to avoid redundant processing, 
                        but this form allows you to override that limit and force immediate processing.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ManualDomainQueue; 